<template>
  <RevDrawer
    :close-button-label="i18n(translations.cancel)"
    :name="modalName"
    :title="i18n(translations.title)"
  >
    <template #body>
      <RevInfoBlock
        v-if="apiError"
        class="mt-16"
        :icon="IconWarning"
        :title="apiError.title"
        variant="danger"
      >
        {{ apiError.detail }}
      </RevInfoBlock>

      <RevForm
        :id="FORM_ID"
        :initial-values="INITIAL_VALUES"
        :validate
        @submit="handleSubmit"
      >
        <template #default="{ values, errors }">
          <p class="body-1-bold my-16">
            {{ i18n(translations.prompt, { orderLineId: orderLine.id }) }}
          </p>

          <RevInputText
            id="imei"
            v-model="values.imei"
            :error="errors.imei"
            :label="i18n(translations.imei)"
            :maxlength="IMEI_MAX_LENGTH"
          />
        </template>
      </RevForm>
    </template>

    <template #footer>
      <div class="flex flex-col items-center">
        <RevButton
          class="mb-16"
          data-qa="confirm-cancel-orderline"
          :form="FORM_ID"
          full-width="always"
          :loading="isLoading"
          type="submit"
          variant="primary"
        >
          {{ i18n(translations.confirm) }}
        </RevButton>

        <RevLink @click="handleCancel">
          {{ i18n(translations.cancel) }}
        </RevLink>
      </div>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api'
import { patchMerchantOrdersOrderline } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants-orders/merchant-orders-page'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputText } from '@ds/components/InputText'
import { RevLink } from '@ds/components/Link'
import { closeModal } from '@ds/components/ModalBase'
import { IconWarning } from '@ds/icons/IconWarning'

import type { OrderLine } from '../../../../../../../../Tab.types'

import {
  FORM_ID,
  IMEI_MAX_LENGTH,
  INITIAL_VALUES,
  MODAL_NAME,
} from './AddOrderLineIMEIDrawer.constants'
import translations from './AddOrderLineIMEIDrawer.translations'

type FormValues = {
  imei: string
}

const props = defineProps<{
  orderLine: OrderLine
  refreshOrders: () => void
}>()

const i18n = useI18n()
const { openSuccessToast } = useTheToast()

const apiError = ref<null | HttpApiError>(null)
const isLoading = ref(false)
const modalName = computed(() => MODAL_NAME + props.orderLine.id)

const validate = makeValidate<FormValues>({
  imei: required(i18n(translations.required)),
})

async function handleSubmit({ imei }: FormValues) {
  try {
    isLoading.value = true
    apiError.value = null

    await $httpFetch(patchMerchantOrdersOrderline, {
      pathParams: {
        orderLineId: props.orderLine.id,
      },
      body: {
        imei,
      },
    })

    openSuccessToast({
      content: i18n(translations.successMessage),
    })

    props.refreshOrders()

    closeModal(modalName.value)
  } catch (error) {
    apiError.value = HttpApiError.fromAnyError(error as Error)
  } finally {
    isLoading.value = false
  }
}

function reset() {
  apiError.value = null
}

function handleCancel() {
  reset()
  closeModal(modalName.value)
}
</script>
